import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Tooltip, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Visibility, 
  Edit, 
  Delete,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/ar';
import 'moment/locale/en-gb';
import { theme } from '../../../theme';
import Can from '../../../permissions/Can';
import MarkAttendance from './MarkAttendance';
import { useTranslation } from 'react-i18next';
import { appointmentStatusLabelKey } from '../../../utils/appointmentWorkflow';

const MonthView = ({ 
  appointments = [], 
  currentDate,
  onShowDetails, 
  onEditAppointment, 
  onDeleteAppointment,
  isEmployee = false
}) => {
  const { t, i18n } = useTranslation();

  // Configure moment locale immediately and on language change
  const setMomentLocale = () => {
    const momentLocale = i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en-gb' : 'fr';
    moment.locale(momentLocale);
    
    // For Arabic, use Latin numerals instead of Arabic numerals
    if (i18n.language === 'ar') {
      moment.updateLocale(momentLocale, {
        preparse: function (string) {
          return string.replace(/[٠-٩]/g, function (match) {
            return match.charCodeAt(0) - 0x0660;
          });
        },
        postformat: function (string) {
          return string.replace(/\d/g, function (match) {
            return String.fromCharCode(match.charCodeAt(0) + 0x0660);
          });
        }
      });
      // Override postformat to keep Latin numerals
      moment.updateLocale(momentLocale, {
        postformat: function (string) {
          return string; // Keep original string with Latin numerals
        }
      });
    }
  };
  
  // Set locale immediately on mount
  setMomentLocale();
  
  // Also update locale when language changes
  useEffect(() => {
    setMomentLocale();
  }, [i18n.language]);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const [selectedDayAppointments, setSelectedDayAppointments] = useState([]);
  const [selectedAppointmentForAction, setSelectedAppointmentForAction] = useState(null);

  const toggleDayExpanded = (dateKey) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  const handleMobileDayClick = (dayAppointments) => {
    setSelectedDayAppointments(dayAppointments);
    setSelectedAppointmentForAction(dayAppointments[0] || null);
    setMobileDialogOpen(true);
  };

  const calendarData = useMemo(() => {
    const startOfMonth = currentDate.clone().startOf('month');
    const endOfMonth = currentDate.clone().endOf('month');
    const startOfWeek = startOfMonth.clone().startOf('week').isoWeekday(1);
    const endOfWeek = endOfMonth.clone().endOf('week').isoWeekday(7);

    const days = [];
    const current = startOfWeek.clone();
    while (current.isSameOrBefore(endOfWeek)) {
      days.push(current.clone());
      current.add(1, 'day');
    }
    return days;
  }, [currentDate]);

  const appointmentsByDate = useMemo(() => {
    const grouped = {};
    appointments.forEach(appointment => {
      const dateKey = moment(appointment.start_time).format('YYYY-MM-DD');
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(appointment);
    });
    return grouped;
  }, [appointments]);

  const getAppointmentsForDate = (date) => {
    const dateKey = date.format('YYYY-MM-DD');
    return appointmentsByDate[dateKey] || [];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return theme.colors.success[500];
      case 'pending': return theme.colors.warning[500];
      case 'cancelled': return theme.colors.error[500];
      case 'completed': return theme.colors.info[500];
      default: return theme.colors.primary[500];
    }
  };

  const getStatusLabel = (status) => {
    return t(appointmentStatusLabelKey(status));
  };

  // Week days in order: Sunday to Saturday for Arabic, Monday to Sunday for others
  const weekDaysOrder = i18n.language === 'ar' 
    ? ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  // Use abbreviated names for mobile, full names for desktop
  const weekDays = weekDaysOrder.map(day => 
    isMobile 
      ? t(`pro_dashboard.week_days.abbrev.${day}`)
      : t(`pro_dashboard.week_days.${day}`)
  );

  return (
    <Box sx={{ 
      backgroundColor: '#fff',
      borderRadius: 3,
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      {/* EN-TÊTE */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        backgroundColor: theme.colors.primary[100],
        borderBottom: `1px solid ${theme.colors.primary[200]}`,
        minWidth: '100%',
      }}>
        {weekDays.map(day => (
          <Box 
            key={day}
            sx={{
              p: { xs: 0.5, md: 1.5 },
              textAlign: 'center',
              fontWeight: 600,
              fontSize: { xs: '0.6rem', md: '0.8rem' },
              color: theme.colors.text.primary,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {day}
          </Box>
        ))}
      </Box>

      {/* GRILLE */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {calendarData.map(date => {
          const dayAppointments = getAppointmentsForDate(date);
          const dateKey = date.format('YYYY-MM-DD');
          const isExpanded = expandedDays.has(dateKey);
          const visibleAppointments = isExpanded ? dayAppointments : dayAppointments.slice(0, 3);
          const isCurrentMonth = date.month() === currentDate.month();
          const isToday = date.isSame(moment(), 'day');

          return (
            <Box
              key={dateKey}
              sx={{
                minHeight: { xs: '80px', md: '120px' },
                p: { xs: 0.5, md: 1 },
                borderRight: `1px solid ${theme.colors.primary[100]}`,
                borderBottom: `1px solid ${theme.colors.primary[100]}`,
                position: 'relative',
                backgroundColor: isToday ? theme.colors.primary[50] : !isCurrentMonth ? '#f9f9f9' : '#fff',
                '&:nth-of-type(7n)': { borderRight: 'none' }
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.7rem', md: '0.875rem' },
                  fontWeight: isToday ? 700 : 500,
                  mb: 0.5,
                  color: !isCurrentMonth ? theme.colors.text.disabled : theme.colors.text.primary
                }}
              >
                {date.format('D')}
              </Typography>

              {/* RDV - Version mobile compacte */}
              {isMobile && dayAppointments.length > 0 ? (
                <Box
                  onClick={() => handleMobileDayClick(dayAppointments)}
                  sx={{
                    backgroundColor: theme.colors.primary[500],
                    color: 'white',
                    borderRadius: 1,
                    p: 0.5,
                    textAlign: 'center',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.colors.primary[600],
                    }
                  }}
                >
                  {dayAppointments.length} {t('pro_dashboard.appointments.short_title')}
                </Box>
              ) : !isMobile && visibleAppointments.map((appointment, index) => (
                <Tooltip
                  key={`${appointment.appointment_id}-${index}`}
                  title={
                    <Box sx={{ p: 1.5, minWidth: 240, fontFamily: 'Roboto, sans-serif' }}>
                      {/* CLIENT */}
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#fff',
                          fontSize: '1rem',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 18 }} />
                        {appointment.client_name}
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                        {/* SERVICE */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 4, height: 4, bgcolor: '#fff', borderRadius: '50%' }} />
                          <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                            <strong>{t('pro_dashboard.services.title')}:</strong> {appointment.service_name}
                          </Typography>
                        </Box>

                        {/* TÉLÉPHONE CLIENT */}
                        {appointment.client_phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: '#ffcc80' }} />
                            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                              <strong>{t('pro_dashboard.client.phone')}:</strong> {appointment.client_phone}
                            </Typography>
                          </Box>
                        )}

                        {/* EMPLOYÉ */}
                        {appointment.team_member_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 14, color: '#90caf9' }} />
                            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                              <strong>{t('pro_dashboard.team.title')}:</strong> {appointment.team_member_name}
                            </Typography>
                          </Box>
                        )}

                        {/* SALON */}
                        {appointment.salon_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 14, color: '#a5d6a7' }} />
                            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                              <strong>{t('pro_dashboard.salon.title')}:</strong> {appointment.salon_name}
                            </Typography>
                          </Box>
                        )}

                        {/* HORAIRE */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 28,
                            height: 28,
                            bgcolor: '#fff',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#333',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                          }}>
                            {moment(appointment.start_time).format('HH')}
                          </Box>
                          <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
                            {moment(appointment.start_time).format('HH:mm')} to {moment(appointment.end_time).format('HH:mm')}
                          </Typography>
                        </Box>

                        {/* DURÉE */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`${moment(appointment.end_time).diff(moment(appointment.start_time), 'minutes')} min`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.2)',
                              color: '#fff',
                              fontWeight: 600,
                              height: 22,
                              fontSize: '0.75rem',
                            }}
                          />
                          <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                            <strong>{t('pro_dashboard.appointments.duration')}</strong>
                          </Typography>
                        </Box>

                        {/* STATUT */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: getStatusColor(appointment.status),
                              color: '#fff',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}
                          >
                            {getStatusLabel(appointment.status)}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  }
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        borderRadius: 2,
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                        border: '1px solid #333',
                        p: 0,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.colors.bg,
                      border: `1px solid ${getStatusColor(appointment.status)}`,
                      borderRadius: 1,
                      p: 0.5,
                      mb: 0.5,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 2px 6px rgba(0,0,0,0.1)`,
                        '& .appointment-actions': { opacity: 1 }
                      }
                    }}
                    onClick={() => onShowDetails?.(appointment)}
                  >
                    <Typography sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: theme.colors.text.secondary,
                      textAlign: 'center'
                    }}>
                      {moment(appointment.start_time).format('HH:mm')}
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: theme.colors.text.primary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'center'
                    }}>
                      {appointment.client_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.3, mt: 0.3, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip 
                        label={getStatusLabel(appointment.status)}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(appointment.status),
                          color: 'white',
                          fontSize: '0.6rem',
                          height: '16px',
                        }}
                      />
                      {appointment.salon_name && (
                        <Chip
                          icon={<BusinessIcon sx={{ fontSize: 7 }} />}
                          label={appointment.salon_name}
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '0.5rem',
                            fontWeight: 600,
                            backgroundColor: theme.colors.secondary[100],
                            color: theme.colors.secondary[700],
                            '& .MuiChip-icon': { fontSize: 7, marginLeft: '2px', color: theme.colors.secondary[600] },
                            '& .MuiChip-label': { padding: '0 3px' }
                          }}
                        />
                      )}
                      {appointment.team_member_name && (
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: 7 }} />}
                          label={appointment.team_member_name}
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '0.5rem',
                            fontWeight: 600,
                            backgroundColor: theme.colors.primary[100],
                            color: theme.colors.primary[700],
                            '& .MuiChip-icon': { fontSize: 7, marginLeft: '2px', color: theme.colors.primary[600] },
                            '& .MuiChip-label': { padding: '0 3px' }
                          }}
                        />
                      )}
                    </Box>

                    {/* BOUTONS */}
                    <Box 
                      className="appointment-actions"
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        display: 'flex',
                        gap: 0.3,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MarkAttendance 
                        appointment={appointment} 
                        onUpdate={() => window.location.reload()}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onShowDetails?.(appointment); }}
                        sx={{ 
                          padding: '1px', 
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          width: '18px',
                          height: '18px',
                          minWidth: 'unset',
                          color: theme.colors.primary[600],
                        }}
                      >
                        <Visibility sx={{ fontSize: '12px' }} />
                      </IconButton>
                      <Can I="update" a="appointments">
                        {!isEmployee && (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onEditAppointment?.(appointment); }}
                            sx={{ 
                              padding: '1px', 
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              width: '18px',
                              height: '18px',
                              minWidth: 'unset',
                              color: theme.colors.warning[600],
                            }}
                          >
                            <Edit sx={{ fontSize: '12px' }} />
                          </IconButton>
                        )}
                      </Can>
                      <Can I="delete" a="appointments">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onDeleteAppointment?.(appointment); }}
                          sx={{ 
                            padding: '1px', 
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            width: '18px',
                            height: '18px',
                            minWidth: 'unset',
                            color: theme.colors.error[600],
                          }}
                        >
                          <Delete sx={{ fontSize: '12px' }} />
                        </IconButton>
                      </Can>
                    </Box>
                  </Box>
                </Tooltip>
              ))}

              {/* +X DE PLUS */}
              {!isMobile && dayAppointments.length > 3 && (
                <Typography
                  onClick={() => toggleDayExpanded(dateKey)}
                  sx={{
                    fontSize: '0.7rem',
                    color: theme.colors.primary[600],
                    fontStyle: 'italic',
                    mt: 0.5,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: theme.colors.primary[700], fontWeight: 600 }
                  }}
                >
                  {isExpanded ? t('common.see_less') : `+${dayAppointments.length - 3} ${t('common.more')}`}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Dialog mobile pour afficher les RDV du jour */}
      <Dialog
        open={mobileDialogOpen}
        onClose={() => {
          setMobileDialogOpen(false);
          setSelectedAppointmentForAction(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiDialog-paper': {
            m: { xs: 0, md: 2 },
            maxHeight: { xs: '100%', md: 'calc(100% - 64px)' },
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '100%', md: '600px' },
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          borderBottom: `2px solid ${theme.colors.primary[100]}`,
        }}>
          {t('pro_dashboard.appointments.day_appointments')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedDayAppointments.map((appointment, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: `2px solid ${getStatusColor(appointment.status)}`,
                backgroundColor: '#f9f9f9',
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': {
                  backgroundColor: theme.colors.primary[50],
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }
              }}
            >
              {/* Boutons d'action en haut à droite */}
              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                <MarkAttendance 
                  appointment={appointment} 
                  onUpdate={() => {
                    setMobileDialogOpen(false);
                    setSelectedAppointmentForAction(null);
                    window.location.reload();
                  }} 
                />
                {!isEmployee && (
                  <>
                    <Tooltip title={t('actions.edit')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditAppointment?.(appointment);
                          setMobileDialogOpen(false);
                          setSelectedAppointmentForAction(null);
                        }}
                        sx={{
                          color: theme.colors.primary[500],
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': {
                            backgroundColor: theme.colors.primary[500],
                            color: '#fff',
                          }
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('actions.delete')}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteAppointment?.(appointment);
                          setMobileDialogOpen(false);
                          setSelectedAppointmentForAction(null);
                        }}
                        sx={{
                          color: theme.colors.error[500],
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': {
                            backgroundColor: theme.colors.error[500],
                            color: '#fff',
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, pr: 8 }}>
                {moment(appointment.start_time).format('HH:mm')} - {appointment.client_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>{t('pro_dashboard.services.title')}:</strong> {appointment.service_name}
              </Typography>
              {appointment.team_member_name && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('pro_dashboard.team.title')}:</strong> {appointment.team_member_name}
                </Typography>
              )}
              {appointment.salon_name && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('pro_dashboard.salon.title')}:</strong> {appointment.salon_name}
                </Typography>
              )}
              <Chip
                label={getStatusLabel(appointment.status)}
                size="small"
                sx={{
                  backgroundColor: getStatusColor(appointment.status),
                  color: 'white',
                  mt: 1,
                }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMobileDialogOpen(false);
            setSelectedAppointmentForAction(null);
          }}>{t('actions.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthView;
